// Data hook for the Work Plan page: one bundle per date, refetched on WS work-plan events
// and on demand. Mutations merge their server responses via applyLocal for latency, with
// the WS-triggered refetch as the consistency net.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchWorkPlanDay } from "@/lib/api/work-plan";
import { subscribeWorkPlanEvents } from "@/lib/realtime/chat-realtime-service";
import { weekStripDates } from "./work-plan-utils";

// Config-level work-plan events (no dates): every open planner refetches so the rail's
// role pills, the session lane/labels, the flag vocabulary and the clinic rows stay current.
const CONFIG_ACTIONS = new Set(["roles_changed", "sessions_changed", "flags_changed", "clinics_changed"]);

export function useWorkPlanDay(date) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const seqRef = useRef(0);
  const dateRef = useRef(date);
  dateRef.current = date;

  useEffect(() => {
    let cancelled = false;
    const seq = ++seqRef.current;
    // Keep the previous day's data on screen while the next loads — no flash.
    setIsLoading(true);
    setError("");
    fetchWorkPlanDay({ date })
      .then((bundle) => {
        if (cancelled || seq !== seqRef.current) return;
        setData(bundle);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled || seq !== seqRef.current) return;
        setError(err instanceof Error ? err.message : "Failed to load the work plan.");
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

  useEffect(
    () =>
      subscribeWorkPlanEvents((detail) => {
        if (!detail) return;
        const action = String(detail.action || "");
        // MC events and config edits always refetch — roles change the rail's pills,
        // sessions the lane/labels, flags the vocabulary and clinic visibility which rows
        // the bundle even lists; posting/publish events only when they touch the visible date.
        const touchesDate = !detail.dates?.length || detail.dates.includes(dateRef.current);
        if (action.startsWith("mc_") || CONFIG_ACTIONS.has(action) || touchesDate) {
          setRefreshKey((key) => key + 1);
        }
      }),
    [],
  );

  /** Merge a mutation's server response into the loaded bundle without waiting. */
  const applyLocal = useCallback((mutate) => {
    setData((current) => (current ? mutate(current) : current));
  }, []);

  const derived = useMemo(() => {
    const sessionsByKey = {};
    for (const session of data?.sessions || []) {
      sessionsByKey[session.key] = session;
    }
    const clinicsById = {};
    for (const clinic of data?.clinics || []) {
      clinicsById[clinic.id] = clinic;
    }
    const staffById = {};
    for (const person of data?.staff || []) {
      staffById[person.id] = person;
    }
    return { sessionsByKey, clinicsById, staffById };
  }, [data]);

  return { data, ...derived, isLoading, error, refresh, applyLocal };
}

// The week strip's data: how many support staff are assigned per Mon–Sat day, cached per
// date for the session. The shown day is always seeded fresh from the live bundle below;
// other days refetch only after invalidateWeekLoad() (cross-day mutations like a weekly
// copy) or navigation, so a stale bar is at worst a few pixels off until then.
const weekLoadCache = new Map(); // dateKey -> distinct assigned support staff

/** Drops every cached day so the next render refetches the strip (call after a copy). */
export function invalidateWeekLoad() {
  weekLoadCache.clear();
}

/** Distinct people with a posting that day — two half-day postings are still one person. */
function assignedStaffCount(bundle) {
  const staffIds = new Set();
  for (const posting of bundle?.postings || []) {
    if (posting?.staffId) staffIds.add(posting.staffId);
  }
  return staffIds.size;
}

/**
 * Mon–Sat of `date`'s week as week-strip tiles ({ date, label, total }), total = assigned
 * support staff. Fetches the day bundle once per uncached day (the handoff's contract:
 * the strip reuses the day bundle). Deliberately UNFILTERED by the device's
 * clinic-visibility preference — like Copy and Publish, the strip describes the real day.
 */
export function useWeekLoad(date, bundle) {
  const [fetchTick, setFetchTick] = useState(0);
  const days = useMemo(() => weekStripDates(date), [date]);

  // The shown day rides the live bundle, so its tile tracks every mutation and refetch.
  const liveTotal = useMemo(() => (bundle ? assignedStaffCount(bundle) : null), [bundle]);
  if (liveTotal !== null && days.length > 0) weekLoadCache.set(date, liveTotal);

  // `bundle` is a dep so a post-invalidation refetch (new bundle identity) re-runs the
  // missing-days check; while the cache is warm the re-run is a no-op.
  useEffect(() => {
    let cancelled = false;
    const missing = days.filter((day) => day.date !== date && !weekLoadCache.has(day.date));
    if (missing.length === 0) return undefined;
    Promise.allSettled(
      missing.map(async (day) => {
        const dayBundle = await fetchWorkPlanDay({ date: day.date });
        weekLoadCache.set(day.date, assignedStaffCount(dayBundle));
      }),
    ).then(() => {
      if (!cancelled) setFetchTick((tick) => tick + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [days, date, bundle]);

  return useMemo(
    () => days.map((day) => ({ ...day, total: weekLoadCache.get(day.date) || 0 })),
    // fetchTick + liveTotal are the cache's change signals — the Map itself is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, liveTotal, fetchTick],
  );
}
